import React from 'react'
import { glassPanelStyle } from './agent/agentChatLayout'

const STOP = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'to',
  'of',
  'in',
  'on',
  'for',
  'is',
  'are',
  'was',
  'were',
  'be',
  'by',
  'at',
  'as',
  'it',
  'if',
  'do',
  'does',
  'did',
  'we',
  'you',
  'your',
  'our',
  'with',
  'from',
  'this',
  'that',
  'what',
  'when',
  'how',
  'will',
  'about',
  'tell',
  'me',
])

/** Escape RegExp special chars. */
function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Tokenize query into highlight terms (skip short/stop words).
 * @param {string} query
 * @returns {string[]}
 */
export function queryHighlightTerms(query) {
  const raw = String(query || '')
    .toLowerCase()
    .split(/[^a-z0-9%._-]+/i)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !STOP.has(t))
  return [...new Set(raw)].slice(0, 12)
}

/**
 * Split text into plain + marked segments for React render.
 * @param {string} text
 * @param {string} query
 * @returns {{ type: 'text'|'mark', value: string }[]}
 */
export function highlightSnippetSegments(text, query) {
  const src = String(text || '')
  const terms = queryHighlightTerms(query)
  if (!src || terms.length === 0) return [{ type: 'text', value: src }]

  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi')
  const parts = src.split(re)
  return parts.filter(Boolean).map((part) => {
    const isMark = terms.some((t) => part.toLowerCase() === t.toLowerCase())
    return { type: isMark ? 'mark' : 'text', value: part }
  })
}

/**
 * @param {number|null|undefined} n
 * @param {number} digits
 */
export function formatScore(n, digits = 3) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return Number(n).toFixed(digits)
}

/**
 * Shared hybrid search result card.
 * @param {object} props
 * @param {object} props.result
 * @param {number} props.rank — 1-based
 * @param {string} props.query
 * @param {number} [props.snippetMax]
 * @param {boolean} [props.compact]
 * @param {'hybrid'|'keyword'|'vector'} [props.scoreMode]
 * @param {(helpful: boolean) => void} [props.onFeedback]
 * @param {boolean|null} [props.feedbackValue] — true=helpful, false=not, null=unset
 * @param {string|null} [props.rankMoveBadge] — rerank A/B badge e.g. ↑2, new, =
 */
export function KbSearchResultCard({
  result: r,
  rank,
  query,
  snippetMax = 400,
  compact = false,
  scoreMode = 'hybrid',
  onFeedback,
  feedbackValue = null,
  rankMoveBadge = null,
}) {
  let text = String(r.chunk_text || '')
  const truncated = text.length > snippetMax
  if (truncated) text = `${text.slice(0, snippetMax)}…`
  const segments = highlightSnippetSegments(text, query)
  const isPersonal = r.library === 'personal' || r.document_metadata?.library === 'personal'
  const showLibraryBadge = r.library != null || r.document_metadata?.library != null

  const scoreLine = (() => {
    if (scoreMode === 'keyword') {
      return <span>keyword score: {formatScore(r.scores?.keyword)}</span>
    }
    if (scoreMode === 'vector') {
      return <span>similarity: {formatScore(r.scores?.vector)}</span>
    }
    return (
      <>
        <span>keyword: {formatScore(r.scores?.keyword)}</span>
        <span>vector: {formatScore(r.scores?.vector)}</span>
        <span title="Reciprocal Rank Fusion — ranking blend, not % relevance">
          rrf: {formatScore(r.scores?.rrf, 4)} (not % relevance)
        </span>
      </>
    )
  })()

  const fbBtn = (helpful, label) => (
    <button
      type="button"
      disabled={feedbackValue != null}
      onClick={() => onFeedback?.(helpful)}
      style={{
        fontSize: 12,
        padding: '4px 8px',
        borderRadius: 6,
        border: '1px solid #cbd5e1',
        background:
          feedbackValue === helpful ? (helpful ? '#dcfce7' : '#fee2e2') : '#fff',
        color: feedbackValue === helpful ? (helpful ? '#15803d' : '#b91c1c') : '#475569',
        cursor: feedbackValue != null ? 'default' : 'pointer',
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  )

  return (
    <article
      style={{
        ...glassPanelStyle,
        padding: compact ? 14 : 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: compact ? 15 : 16, fontWeight: 700, color: '#0f172a' }}>
          {rank}. {r.title}{' '}
          {showLibraryBadge ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: isPersonal ? '#7c3aed' : '#475569',
                background: isPersonal ? '#f5f3ff' : '#f1f5f9',
                border: isPersonal ? '1px solid #ddd6fe' : '1px solid #e2e8f0',
                borderRadius: 4,
                padding: '1px 6px',
                verticalAlign: 'middle',
              }}
            >
              {isPersonal ? 'Personal' : 'Org'}
            </span>
          ) : null}
        </h2>
        <span
          style={{
            fontSize: 12,
            color: '#475569',
            whiteSpace: 'nowrap',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {rankMoveBadge ? (
            <span
              title="Rank change vs Hybrid (no rerank)"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color:
                  rankMoveBadge === 'new'
                    ? '#7c3aed'
                    : rankMoveBadge.startsWith('↑')
                      ? '#15803d'
                      : rankMoveBadge.startsWith('↓')
                        ? '#b91c1c'
                        : '#64748b',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: 4,
                padding: '1px 5px',
              }}
            >
              {rankMoveBadge}
            </span>
          ) : null}
          rank #{rank}
        </span>
      </div>

      {r.section_heading ? (
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>
          Section: {r.section_heading}
        </div>
      ) : null}

      <p style={{ margin: 0, fontSize: 14, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {segments.map((seg, i) =>
          seg.type === 'mark' ? (
            <mark
              key={i}
              style={{
                background: '#fef08a',
                color: 'inherit',
                padding: '0 2px',
                borderRadius: 2,
              }}
            >
              {seg.value}
            </mark>
          ) : (
            <span key={i}>{seg.value}</span>
          )
        )}
      </p>

      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: '#64748b',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 16px',
        }}
      >
        {scoreLine}
      </div>

      <footer
        style={{
          marginTop: 10,
          fontSize: 12,
          color: '#94a3b8',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
        }}
      >
        <span>
          Citation · document {r.document_id} · chunk {r.chunk_id}
        </span>
        {typeof onFeedback === 'function' ? (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: '#94a3b8' }}>Helpful?</span>
            {fbBtn(true, 'Yes')}
            {fbBtn(false, 'No')}
          </span>
        ) : null}
      </footer>
    </article>
  )
}

/** Shared meta banner body for Search / Playground. */
export function hybridMetaBannerText(meta) {
  if (!meta) return ''
  const mode = meta.hybrid ? 'Hybrid (keyword + vector)' : 'Keyword only'
  const arms = meta.arms
    ? ` · Keyword arm: ${meta.arms.keyword ? 'yes' : 'no'} · Vector arm: ${meta.arms.vector ? 'yes' : 'no'}`
    : ''
  const thr =
    meta.match_threshold != null && !Number.isNaN(Number(meta.match_threshold))
      ? ` · threshold ${Number(meta.match_threshold).toFixed(2)}`
      : ''
  const vecErr = meta.vector_error ? ` — vector: ${meta.vector_error}` : ''
  const kwErr = meta.keyword_error ? ` — keyword: ${meta.keyword_error}` : ''
  return `${mode}${arms}${thr}${kwErr}${vecErr}. Four columns: keyword, semantic, hybrid (RRF), hybrid (+ Cohere rerank).`
}

/** Playground rerank A/B summary line. */
export function rerankCompareBannerText(rerankMeta, rankMoves, candidateCount) {
  if (!rerankMeta) return ''
  const pool = candidateCount != null ? ` · pool ${candidateCount}` : ''
  if (rerankMeta.rerank_used) {
    const model = rerankMeta.rerank_model ? ` model ${rerankMeta.rerank_model}` : ''
    const moves = rankMoves
      ? ` · Rank moves: ${rankMoves.up} up · ${rankMoves.down} down · ${rankMoves.newInTopN} new · ${rankMoves.droppedFromTopN} dropped`
      : ''
    return `Rerank A/B: OFF (forced)${pool} · ON rerank_used=yes${model}${moves}`
  }
  const warn = rerankMeta.rerank_warning
    ? ` · ${rerankMeta.rerank_warning}`
    : ' · Rerank skipped (no Cohere key or error) — column 4 matches column 3'
  return `Rerank A/B: OFF (forced)${pool} · ON rerank_used=no${warn}`
}

/** Compact Playground column shell. */
export function PlaygroundArmColumn({ title, subtitle, children, searching }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 240,
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minHeight: 200,
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid #e2e8f0',
          background: '#fff',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{title}</div>
        {subtitle ? <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{subtitle}</div> : null}
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        {children}
      </div>
      {searching ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(248,250,252,0.65)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            color: '#475569',
            fontWeight: 600,
          }}
        >
          Searching…
        </div>
      ) : null}
    </div>
  )
}
